import { PointData, Texture } from "pixi.js";
import { BeeType } from "./BeeType";
import { Enemy } from "./Enemy";
import { Game } from "./Game";
import { Geom } from "./Geom";
import { Keyboard } from "./Keyboard";
import { Player } from "./Player";
import { Sheets } from "./Textures";

export enum ActiveBeeCategory {
	Passive,
	Ranged,
	Grenade,
	Melee,
	Flame,
	Dodge,
	Shield,
	Throw
}

export class CoolDownBehaviour implements AbilityBehaviour {
	private cooldown = 0;

	constructor(private readonly duration: number, private readonly modifier: (count: number) => number) {
	}

	public getCooldownPercent(): number {
		return Math.max(Math.min((this.duration - this.cooldown) / this.duration, 1), 0);
	}

	public tick(count: number, bee: AbilityBee, game: Game, deltaMS: number): boolean {
		if (this.cooldown > 0) {
			this.cooldown -= deltaMS * this.modifier(count);
			game.player.filthTracker.soil();
			return true;
		}
		return false;
	}

	public invoke(count: number, bee: AbilityBee, game: Game): void {
		this.cooldown = this.duration
	}

}

export class ButtonPressedBehaviour implements AbilityBehaviour {
	constructor(private button: keyof Keyboard['keys']) {
	}

	public tick(count: number, bee: AbilityBee, game: Game, deltaMS: number): boolean {
		if (!game.keys?.[this.button].pressed) {
			return true;
		}
		return false;
	}
}

export class MousePressedBehaviour implements AbilityBehaviour {
	public tick(count: number, bee: AbilityBee, game: Game, deltaMS: number): boolean {
		if (!game.mouse.mouseClicked) {
			return true;
		}
		return false;
	}
}

export class FireBulletConsequence implements AbilityConsequence {
	invoke(count: number, bee: AbilityBee, game: Game, targets: Enemy[]): void {
		game.player.fireBullet(game, count * 2, game.mouse.mouseClicked)
	}

	tick(count: number, bee: AbilityBee, game: Game, deltaMS: number): boolean {
		if (game.mouse.mouseMoved) {
			game.player.aimReticle(game.mouse.mouseMoved)
		}
		return false;
	}
}

interface AbilityConsequence {
	invoke?: (count: number, bee: AbilityBee, game: Game, targets: Enemy[]) => void;
	tick?: (count: number, bee: AbilityBee, game: Game, deltaMS: number) => boolean,
}

interface AbilityTarget {
	getTargets(bee: AbilityBee, game: Game): Enemy[]
}

export class MeleeTarget implements AbilityTarget {
	private readonly consequences: AbilityBehaviour[];

	constructor(...consequences: AbilityBehaviour[]) {
		this.consequences = consequences;
	}

	public getTargets(bee: AbilityBee, game: Game): Enemy[] {
		return game.enemies.filter(enemy => Geom.distance(enemy, game.player) < game.player.size + enemy.size)
	}
}

export class BasicDamage implements AbilityConsequence {
	private flashingMax = 100;
	private flashing = 0;

	invoke(count: number, bee: AbilityBee, game: Game, targets: Enemy[]): void {
		for (const target of targets) {
			target.dealDamage(game, count * 3);
		}
		this.flashing = this.flashingMax;
		for (let beeDisplay of game.player.beeDisplays) {
			if (beeDisplay.activeSprite) {
				beeDisplay.attackOffset = 5;
				beeDisplay.activeSprite.x += 5;
				if (beeDisplay.activeSprite?.scale) beeDisplay.activeSprite.scale = 1.2;
			}
		}
		// game.player.shade.color = 0xffffff;
	}

	tick(count: number, bee: AbilityBee, game: Game, deltaMS: number): boolean {
		if (this.flashing > 0) {
			this.flashing -= deltaMS;
			if (this.flashing < 0) {
				for (let beeDisplay of game.player.beeDisplays) {
					beeDisplay.attackOffset = 0;
					if (beeDisplay.activeSprite?.scale) beeDisplay.activeSprite.scale = 1;
				}
			}
		}
		return false
	}
}

export class PlayerInvincibility
	implements AbilityConsequence {
	private invincible = 0;

	constructor(private readonly duration: number) {
	}

	invoke(count: number, bee: AbilityBee, game: Game, targets: Enemy[]): void {
		game.player.shade.color = 0xffffff;
		this.invincible = this.duration + (count * 20);
	}

	tick(count: number, bee: AbilityBee, game: Game, deltaMS: number): boolean {
		if (this.invincible > 0) {
			this.invincible -= deltaMS;
			if (this.invincible <= 0) {
				game.player.shade.color = 0x000000;
			}
		}
		return false
	}
}

export function tickAbility(count: number, bee: AbilityBee, game: Game, deltaMS: number): void {
	for (const consequences of bee.consequences ?? []) {
		consequences.tick?.(count, bee, game, deltaMS);
	}

	let cancel = false;
	for (const behaviour of bee.behaviours ?? []) {
		if (behaviour.tick?.(count, bee, game, deltaMS) && !cancel) {
			cancel = true;
		}
	}
	if (cancel) {
		return;
	}

	const targets = bee.targets?.map(t => t.getTargets(bee, game)).flat() ?? []

	for (const consequences of bee.consequences ?? []) {
		consequences.invoke?.(count, bee, game, targets);
	}

	for (const behaviour of bee.behaviours ?? []) {
		behaviour.invoke?.(count, bee, game);
	}
}

interface AbilityBehaviour {
	tick?: (count: number, bee: AbilityBee, game: Game, deltaMS: number) => boolean
	invoke?: (count: number, bee: AbilityBee, game: Game) => void
}

export interface AbilityBee {
	texture(): Texture,

	textureCoords: PointData,
	category: ActiveBeeCategory,
	onAcquired?: (player: Player) => void,
	behaviours?: AbilityBehaviour[],
	targets?: AbilityTarget[],
	consequences?: AbilityConsequence[],
	onLost?: (player: Player) => void,
	deathLayer?: number, // Higher layers are considered first.
	deathWeighting?: number, // Weighting if multiple bees has the same layer.
}

export class DashConsequence implements AbilityConsequence {
	private direction?: number;
	private movementLeft?: number;

	constructor(private readonly distance: number, private readonly pixelsPerMillisecond: number) {
	}

	invoke(count: number, bee: AbilityBee, game: Game, targets: Enemy[]): void {
		// const motion = Geom.multiply(Geom.normalize(game.mouse.lastMouseLocation), this.distance)
		// const candidates = game.enemies.filter(e =>
		// 	Geom.distance(e, game.player) <= this.distance + (game.player.size + e.size) / 2);

		// const hits = candidates.filter(candidate => {
		// 	const relativeLocation = Geom.subtract(candidate, game.player)
		// 	const closestPoint = Geom.project(relativeLocation, motion)
		// 	const k = Math.abs(relativeLocation.x) > Math.abs(relativeLocation.x)
		// 		? closestPoint.x / relativeLocation.x
		// 		: closestPoint.y / relativeLocation.y;
		// 	if (k <= 0) {
		// 		return false;
		// 	}

		// 	if (k >= 1) {
		// 		return false;
		// 	}

		// 	return Geom.distance(relativeLocation, closestPoint) < candidate.size / 2 + game.player.size / 2
		// })

		this.direction = Geom.angle({ x: 0, y: 0 }, game.mouse.lastMouseLocation)
		this.movementLeft = this.distance + (count * 20)
		return;
	}

	tick(count: number, bee: AbilityBee, game: Game, deltaMS: number): boolean {
		if (!this.movementLeft || this.movementLeft < 0) {
			return false;
		}

		for (const enemy of game.enemies) {
			if (Geom.distance(enemy, game.player) < game.player.size + enemy.size) {
				enemy.dealDamage(game, Math.round(count / 3))
			}
		}

		const movement = Math.min(this.movementLeft, this.pixelsPerMillisecond * deltaMS)
		this.movementLeft -= movement;
		Geom.moveInDirection(game.player, this.direction!, movement);
		return false;
	}
}

export const Bees: Record<BeeType, AbilityBee> = {
	[BeeType.DiesFirstBee]: {
		category: ActiveBeeCategory.Passive,
		texture: () => Sheets.bees[0],
		deathLayer: 1,
		textureCoords: { x: 0, y: 0 }
	},
	[BeeType.BasicBee]: {
		category: ActiveBeeCategory.Passive,
		texture: () => Sheets.bees[0],
		textureCoords: { x: 0, y: 0 },
	},
	[BeeType.DodgeBee]: {
		texture: () => Sheets.bees[1],
		textureCoords: { x: 1, y: 0 },
		category: ActiveBeeCategory.Dodge,
		behaviours: [
			new ButtonPressedBehaviour("shift"),
			new CoolDownBehaviour(5000, c => 1),
		],
		consequences: [
			new PlayerInvincibility(2000),
		]
	},
	[BeeType.LaserBee]: {
		texture: () => Sheets.bees[2],
		textureCoords: { x: 2, y: 0 },
		category: ActiveBeeCategory.Ranged,
		behaviours: [
			new MousePressedBehaviour(),
			new CoolDownBehaviour(1000, c => 1),
		],
		consequences: [
			new FireBulletConsequence(),
		],
		onLost: (player) => {
			player.aimingReticle.container.visible = false;
		},
		onAcquired: (player) => {
			player.aimingReticle.container.visible = true;
		}
	},
	[BeeType.MeleeBee]: {
		texture: () => Sheets.bees[3],
		textureCoords: { x: 3, y: 0 },
		category: ActiveBeeCategory.Melee,
		behaviours: [
			new ButtonPressedBehaviour("space"),
			new CoolDownBehaviour(1000, c => 1),
		],
		targets: [
			new MeleeTarget(),
		],
		consequences: [
			new BasicDamage(),
		]
	},
	[BeeType.DashBee]: {
		texture: () => Sheets.bees[4],
		textureCoords: { x: 4, y: 0 },
		category: ActiveBeeCategory.Passive,
		behaviours: [
			new ButtonPressedBehaviour("shift"),
			new CoolDownBehaviour(2000, c => 1 + c / 2),
		],
		consequences: [
			new DashConsequence(50, 1),
			new PlayerInvincibility(200)
		],
	},
	[BeeType.BulletBee]: {
		texture: () => Sheets.bees[5],
		textureCoords: { x: 5, y: 0 },
		category: ActiveBeeCategory.Ranged,
		behaviours: [
			new MousePressedBehaviour(),
			new CoolDownBehaviour(1000, c => 1 + c / 2),
		],
		consequences: [
			new FireBulletConsequence(),
		],
		onLost: (player) => {
			player.aimingReticle.container.visible = false;
		},
		onAcquired: (player) => {
			player.aimingReticle.container.visible = true;
		}
	},
};
